import axiosInstance from './axiosConfig';

/**
 * BIR ATC reference data.
 *
 * Replaces the hardcoded ATC lists that were duplicated across formAPI.js,
 * Form2307Form.jsx and ITRManagement.jsx. Those lists embedded the rate inside
 * a display string ('WI160 - EWT 5%'), so nothing could read it — which is why
 * withholdingRate was hand-keyed and never cross-checked. The server now
 * returns `rateBasisPoints` / `ratePercent` as real numbers.
 */
const atcCodeAPI = {
    /**
     * @param {object} params
     * @param {string} [params.formType] - '2306' | '2307' | '1601EQ'. Several
     *   may be passed as a comma-separated list ('2306,2307') when a line can
     *   carry a certificate of either kind.
     * @param {'individual'|'corporation'} [params.payeeClass] - narrows to the
     *   ATCs valid for that payee classification, plus rows marked 'any'.
     * @param {string} [params.asOf] - ISO date; defaults server-side to today.
     *   Pass the record's transaction date so a historical record resolves the
     *   rate that was in force when it was filed, not today's rate.
     * @param {string} [params.q] - case-insensitive substring match on code or
     *   description. Narrows the filters above rather than replacing them.
     */
    list: async ({ formType, asOf, payeeClass, q } = {}) => {
        try {
            const response = await axiosInstance.get('/atc-codes', {
                params: {
                    ...(formType && { formType }),
                    ...(asOf && { asOf }),
                    ...(payeeClass && { payeeClass }),
                    ...(q && { q }),
                },
            });
            return response.data.atcCodes || [];
        } catch (error) {
            console.error('ATC Code API Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to fetch ATC codes';
        }
    },

    /**
     * Resolve one ATC's rate. Returns null when the code is unknown for that
     * date — the server deliberately does not substitute a fallback rate.
     */
    resolveRate: async (code, { formType, asOf } = {}) => {
        try {
            const response = await axiosInstance.get(`/atc-codes/${encodeURIComponent(code)}/rate`, {
                params: { ...(formType && { formType }), ...(asOf && { asOf }) },
            });
            return response.data.atc;
        } catch (error) {
            if (error.response?.status === 404) return null;
            console.error('ATC Rate API Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to resolve ATC rate';
        }
    },

    // ── Superadmin maintenance (routes/admin/atcCodes.js) ──────────────────
    // Separate from list() above: that one is the dropdown feed and returns only
    // rows in force today. These return the full table, superseded rows included.
    admin: {
        /** @param {{formType?:string, code?:string, isActive?:'true'|'false'}} params */
        list: async (params = {}) => {
            try {
                const response = await axiosInstance.get('/admin/atc-codes', { params });
                return {
                    atcCodes: response.data.atcCodes || [],
                    needsReviewCount: response.data.needsReviewCount || 0,
                };
            } catch (error) {
                console.error('ATC Admin List Error:', error.response?.data || error.message);
                throw error.response?.data?.message || 'Failed to load ATC codes';
            }
        },

        create: async (payload) => {
            try {
                const response = await axiosInstance.post('/admin/atc-codes', payload);
                return response.data.atcCode;
            } catch (error) {
                throw error.response?.data?.message || 'Failed to create ATC code';
            }
        },

        update: async (id, payload) => {
            try {
                const response = await axiosInstance.put(`/admin/atc-codes/${id}`, payload);
                return response.data.atcCode;
            } catch (error) {
                throw error.response?.data?.message || 'Failed to update ATC code';
            }
        },

        /**
         * Record that this rate has been confirmed. Clears the "needs review"
         * warning without touching `issuance` — provenance and sign-off are
         * separate facts. reviewNote is required by the server.
         */
        approve: async (id, reviewNote) => {
            try {
                const response = await axiosInstance.post(`/admin/atc-codes/${id}/approve`, { reviewNote });
                return response.data.atcCode;
            } catch (error) {
                throw error.response?.data?.message || 'Failed to approve ATC code';
            }
        },

        /** Withdraw an approval given in error. */
        revokeApproval: async (id) => {
            try {
                const response = await axiosInstance.delete(`/admin/atc-codes/${id}/approve`);
                return response.data.atcCode;
            } catch (error) {
                throw error.response?.data?.message || 'Failed to withdraw approval';
            }
        },

        /** Deactivates — the server never hard-deletes, because filed records reference these codes. */
        deactivate: async (id) => {
            try {
                const response = await axiosInstance.delete(`/admin/atc-codes/${id}`);
                return response.data.atcCode;
            } catch (error) {
                throw error.response?.data?.message || 'Failed to deactivate ATC code';
            }
        },
    },
};

export default atcCodeAPI;
